import { useEffect, useState } from "react";
import styles from "./Table.module.css";
import { useTableManagerContext } from "../table-manager/TableManagerContext";
import RenderRecordsCell from "./RenderRecordsCell";
import arrow from "../icons/chevron-dropdown.svg";

const TableBody = ({
  tooltipConfig,
  records: directRecords,
  columns: directColumns,
  contrastRow,
  selectable,
  selected,
  handleSelectedChange,
  tableDataProps,
  alreadySelectedRecords,
  alreadySelectedIds,
  recordsToDisable,
  isRecordSelectable = () => true,
  expandable = false,
  renderExpandedRow,
  defaultExpandedId = null,
}) => {
  const { records = directRecords, columnsToDisplay = directColumns } =
    useTableManagerContext();

  const [expandedRowKey, setExpandedRowKey] = useState(defaultExpandedId);
  const [hasIntroPlayed, setHasIntroPlayed] = useState(false);

  useEffect(() => {
    if (!expandable || hasIntroPlayed || records.length === 0) return;

    const firstRowKey = records[0].id || JSON.stringify(records[0]) + 0;

    setHasIntroPlayed(true);
    const openTimer = setTimeout(() => setExpandedRowKey(firstRowKey), 300);
    const closeTimer = setTimeout(() => setExpandedRowKey(null), 1400);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
  }, [expandable, hasIntroPlayed, records]);

  const toggleExpanded = (rowKey) => {
    setExpandedRowKey((prev) => (prev === rowKey ? null : rowKey));
  };

  return (
    <tbody
      className={[styles.tbody, contrastRow && styles.contrastRow].join(" ")}
    >
      {records.map((record, index) => {
        const jsonString = JSON.stringify(record);
        const rowKey = record.id || jsonString + index;
        const recordId = alreadySelectedIds
          ? record[alreadySelectedIds?.id]
          : null;
        const isAlreadySelected = alreadySelectedRecords.includes(
          String(recordId),
        );
        const showCheckbox = isRecordSelectable(record);
        const isExpanded = expandedRowKey === rowKey;

        return (
          <>
            <tr
              key={rowKey}
              className={styles.record}
              style={{
                backgroundColor: selected[jsonString] && "#f1f4fb",
                pointerEvents: recordsToDisable.includes(record.id)
                  ? "none"
                  : "auto",
                cursor: recordsToDisable.includes(record.id)
                  ? "not-allowed"
                  : "auto",
              }}
            >
              {selectable && (
                <td align="center" className={styles.check}>
                  {showCheckbox && (
                    <input
                      type="checkbox"
                      id={jsonString}
                      disabled={
                        alreadySelectedIds && !alreadySelectedIds.editable
                          ? isAlreadySelected
                          : false
                      }
                      {...(alreadySelectedIds && alreadySelectedIds?.editable
                        ? { checked: selected[jsonString] }
                        : {
                            checked: selected[jsonString] || isAlreadySelected,
                          })}
                      onChange={handleSelectedChange}
                      title={
                        selected[jsonString] || isAlreadySelected
                          ? "already selected"
                          : "select"
                      }
                      className={styles.checkbox}
                    />
                  )}
                </td>
              )}
              {columnsToDisplay.map(
                (
                  {
                    _title,
                    accessor,
                    dataType,
                    UIComponent,
                    WrapperComponent,
                    formatFunction,
                    lines,
                    toolTip = true,
                    ...rest
                  },
                  colIndex,
                ) => {
                  return (
                    <td
                      className={styles.tooltip}
                      {...(tableDataProps ? tableDataProps(accessor) : {})}
                      key={
                        UIComponent
                          ? UIComponent.name + colIndex
                          : (record[accessor] || "--").toString() + accessor
                      }
                    >
                      <div
                        style={
                          expandable && colIndex === 0
                            ? {
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }
                            : undefined
                        }
                      >
                        {expandable && colIndex === 0 && (
                          <img
                            onClick={() => toggleExpanded(rowKey)}
                            src={arrow}
                            className={[
                              styles.chevron,
                              isExpanded && styles.chevronOpen,
                            ].join(" ")}
                          />
                        )}
                        <RenderRecordsCell
                          tooltipConfig={{
                            ...tooltipConfig,
                            enabled: tooltipConfig.enabled && toolTip,
                            disabledByColumn: toolTip === false,
                          }}
                          record={record}
                          accessor={accessor}
                          dataType={dataType}
                          UIComponent={UIComponent}
                          WrapperComponent={WrapperComponent}
                          formatFunction={formatFunction}
                          rest={rest}
                          lines={lines}
                        />
                      </div>
                    </td>
                  );
                },
              )}
            </tr>
            {expandable && isExpanded && (
              <tr className={styles.expandedRow}>
                <td
                  colSpan={columnsToDisplay.length + (selectable ? 1 : 0)}
                  className={styles.expandedCell}
                >
                  {renderExpandedRow
                    ? renderExpandedRow({ record, records, index, rowKey })
                    : null}
                </td>
              </tr>
            )}
          </>
        );
      })}
    </tbody>
  );
};

export default TableBody;
